"use client";

import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CopyIcon } from "@/components/ui/copy";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { PlusIcon, EllipsisIcon } from "lucide-react";
import { toast } from "sonner";

// Profile → API Keys (docs/MCP-DOMAINS.md §4). Multiple, independently
// named, independently revocable/rotatable personal MCP keys — distinct
// from the single internal key that backs Managed Agent sessions. Create
// and rotate go through /api/profile/mcp-keys/* (need the server-only
// derivation secret, MCP_AGENT_INTERNAL_KEY_SECRET); everything else here
// is a plain, RLS-authorized supabase-js call, per the design-skill's
// "simple mutations = direct .update()/.insert()/.delete()" convention.

type PersonalKey = {
  id: string;
  name: string;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
};

type Grant = {
  id: string;
  resource_id: string;
  allowed_tools: string[];
};

type GrantableDomain = { id: string; name: string; description: string; mcp_tools: string[] };

const TOOL_OPTIONS: { id: string; label: string }[] = [
  { id: "ask", label: "Ask" },
  { id: "list_files", label: "List files" },
  { id: "ask_meeting_minutes", label: "Ask meeting minutes" },
  { id: "list_meeting_minutes", label: "List meeting minutes" },
];

const toolLabel = (id: string) => TOOL_OPTIONS.find((t) => t.id === id)?.label ?? id;

/** What the grant actually buys — the same intersection /api/mcp/k/[keyId] applies. */
const effectiveTools = (grant: Grant, domain: GrantableDomain | undefined) =>
  domain ? grant.allowed_tools.filter((t) => domain.mcp_tools.includes(t)) : grant.allowed_tools;

/** Shown once, right after create/rotate — the derived value is never stored, so this is the only chance to see it. */
function RevealValueDialog({
  open,
  onOpenChange,
  title,
  value,
  url,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  value: string;
  url?: string;
}) {
  const copy = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copied.`))
      .catch(() => toast.error(`Failed to copy ${label.toLowerCase()}.`));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            This value is shown only once and never stored — copy it now. If you lose it, rotate
            the key to get a new one.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-3">
          {url ? (
            <div className="min-w-0 space-y-1.5">
              <Label className="text-xs text-muted-foreground">Server URL</Label>
              <div className="flex min-w-0 items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-muted px-3 py-2 text-xs">{url}</code>
                <Button type="button" size="icon" variant="outline" onClick={() => copy(url, "URL")}>
                  <CopyIcon size={14} />
                </Button>
              </div>
            </div>
          ) : null}
          <div className="min-w-0 space-y-1.5">
            <Label className="text-xs text-muted-foreground">Bearer token</Label>
            <div className="flex min-w-0 items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-muted px-3 py-2 text-xs">{value}</code>
              <Button type="button" size="icon" variant="outline" onClick={() => copy(value, "Token")}>
                <CopyIcon size={14} />
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ManageGrantsDialog({
  open,
  onOpenChange,
  keyId,
  keyName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyId: string;
  keyName: string;
}) {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [domains, setDomains] = useState<GrantableDomain[]>([]);
  const [domainsById, setDomainsById] = useState<Map<string, GrantableDomain>>(new Map());
  const [loading, setLoading] = useState(true);
  const [addDomainId, setAddDomainId] = useState("");
  const [addTools, setAddTools] = useState<string[]>(["ask", "list_files"]);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [grantsRes, domainsRes] = await Promise.all([
      supabase.from("user_mcp_key_grants").select("id, resource_id, allowed_tools").eq("key_id", keyId),
      supabase.from("domains").select("id, name, description, mcp_tools").is("revoked_at", null),
    ]);
    if (grantsRes.error) console.error("[ManageGrantsDialog] load grants failed:", grantsRes.error);
    if (domainsRes.error) console.error("[ManageGrantsDialog] load domains failed:", domainsRes.error);
    setGrants((grantsRes.data as Grant[] | null) ?? []);
    const domainRows = ((domainsRes.data as GrantableDomain[] | null) ?? []).map((d) => ({
      ...d,
      mcp_tools: Array.isArray(d.mcp_tools) ? d.mcp_tools : [],
    }));
    setDomains(domainRows);
    setDomainsById(new Map(domainRows.map((d) => [d.id, d])));
    setLoading(false);
  }, [keyId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) void load();
  }, [open, load]);

  const grantedDomainIds = new Set(grants.map((g) => g.resource_id));
  const availableDomains = domains.filter((d) => !grantedDomainIds.has(d.id));

  // Only offer the tools the domain itself exposes. Agent-backed domains have
  // no meeting recordings, so ask/list_meeting_minutes are never registered for
  // them (src/server/anthropic/mcp-agent-tools.ts) and /api/mcp/k/[keyId] would
  // intersect them straight back out of the grant.
  const selectedDomain = domainsById.get(addDomainId);
  const selectableTools = selectedDomain
    ? TOOL_OPTIONS.filter((t) => selectedDomain.mcp_tools.includes(t.id))
    : [];

  const handleSelectDomain = (domainId: string) => {
    setAddDomainId(domainId);
    const exposed = domainsById.get(domainId)?.mcp_tools ?? [];
    const defaults = ["ask", "list_files"].filter((t) => exposed.includes(t));
    setAddTools(defaults.length > 0 ? defaults : exposed);
  };

  const handleAdd = async () => {
    if (!addDomainId) return;
    setAdding(true);
    const { error } = await supabase
      .from("user_mcp_key_grants")
      .insert({ key_id: keyId, resource_type: "domain", resource_id: addDomainId, allowed_tools: addTools });
    setAdding(false);
    if (error) {
      console.error("[ManageGrantsDialog] add grant failed:", error);
      toast.error("Failed to grant access — you may not have access to that domain yourself.");
      return;
    }
    setAddDomainId("");
    toast.success("Domain granted.");
    void load();
  };

  const handleRemove = async (grantId: string) => {
    setRemovingId(grantId);
    const { error } = await supabase.from("user_mcp_key_grants").delete().eq("id", grantId);
    setRemovingId(null);
    if (error) {
      console.error("[ManageGrantsDialog] remove grant failed:", error);
      toast.error("Failed to remove grant.");
      return;
    }
    toast.success("Grant removed.");
    void load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">{keyName}</DialogTitle>
          <DialogDescription className="text-xs">
            Domains this key can reach, and the tools it can use on each. A key can never reach a
            domain you can&apos;t see yourself.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-6"><Spinner className="size-5 text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            {grants.length === 0 ? (
              <p className="border-t border-border py-4 text-sm text-muted-foreground">
                No domains granted yet.
              </p>
            ) : (
              <div className="border-t border-border">
                {grants.map((grant) => (
                  <div
                    key={grant.id}
                    className="group flex items-center justify-between gap-3 border-b border-border py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-foreground">
                        {domainsById.get(grant.resource_id)?.name ?? "Unknown domain"}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {effectiveTools(grant, domainsById.get(grant.resource_id))
                          .map(toolLabel)
                          .join(", ") || "No tools"}
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 shrink-0 px-2 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-destructive"
                      disabled={removingId === grant.id}
                      onClick={() => void handleRemove(grant.id)}
                    >
                      {removingId === grant.id ? <Spinner className="size-3.5" /> : "Remove"}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-xs font-normal text-muted-foreground">Grant a domain</Label>
              {availableDomains.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No more domains available to grant.
                </p>
              ) : (
                <>
                  <Select value={addDomainId} onValueChange={handleSelectDomain}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a domain…" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDomains.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedDomain && selectableTools.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      This domain exposes no tools over MCP.
                    </p>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {selectableTools.map((tool) => (
                      <label
                        key={tool.id}
                        className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground"
                      >
                        <Checkbox
                          checked={addTools.includes(tool.id)}
                          onCheckedChange={(checked) =>
                            setAddTools((prev) =>
                              checked ? [...prev, tool.id] : prev.filter((t) => t !== tool.id)
                            )
                          }
                        />
                        {tool.label}
                      </label>
                    ))}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!addDomainId || addTools.length === 0 || adding}
                    onClick={() => void handleAdd()}
                  >
                    {adding ? <Spinner className="size-3.5" /> : <PlusIcon size={14} />}
                    Grant
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApiKeysSection({ accessToken }: { accessToken: string }) {
  const [keys, setKeys] = useState<PersonalKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealTitle, setRevealTitle] = useState<string | null>(null);
  const [revealValue, setRevealValue] = useState("");
  const [revealUrl, setRevealUrl] = useState<string | undefined>(undefined);
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [manageGrantsFor, setManageGrantsFor] = useState<PersonalKey | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("user_mcp_personal_keys")
      .select("id, name, created_at, revoked_at, last_used_at")
      .order("created_at", { ascending: false });
    if (error) console.error("[ApiKeysSection] load keys failed:", error);
    setKeys((data as PersonalKey[] | null) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await fetch("/api/profile/mcp-keys", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.error ?? "Failed to create key.");
        return;
      }
      setCreateOpen(false);
      setCreateName("");
      setRevealTitle(`"${name}" created`);
      setRevealValue(body.value);
      setRevealUrl(body.url);
      void load();
    } catch (error) {
      console.error("[ApiKeysSection] create key failed:", error);
      toast.error("Failed to create key.");
    } finally {
      setCreating(false);
    }
  };

  const handleRotate = async (key: PersonalKey) => {
    setRotatingId(key.id);
    try {
      const res = await fetch(`/api/profile/mcp-keys/${key.id}/rotate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.error ?? "Failed to rotate key.");
        return;
      }
      setRevealTitle(`"${key.name}" rotated`);
      setRevealValue(body.value);
      setRevealUrl(undefined);
      void load();
    } catch (error) {
      console.error("[ApiKeysSection] rotate key failed:", error);
      toast.error("Failed to rotate key.");
    } finally {
      setRotatingId(null);
    }
  };

  const handleRevoke = async (key: PersonalKey) => {
    setRevokingId(key.id);
    const { error } = await supabase
      .from("user_mcp_personal_keys")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", key.id);
    setRevokingId(null);
    if (error) {
      console.error("[ApiKeysSection] revoke key failed:", error);
      toast.error("Failed to revoke key.");
      return;
    }
    toast.success("Key revoked.");
    void load();
  };

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">API keys</h2>
          <p className="text-xs text-muted-foreground">
            Personal keys for external MCP clients. Each one reaches only the domains you grant it.
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={() => setCreateOpen(true)}>
          <PlusIcon size={14} />
          Create key
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Spinner className="size-5 text-muted-foreground" /></div>
      ) : keys.length === 0 ? (
        <p className="border-t border-border py-6 text-sm text-muted-foreground">No API keys yet.</p>
      ) : (
        <div className="border-t border-border">
          {keys.map((key) => (
            <div
              key={key.id}
              className="group flex items-center justify-between gap-4 border-b border-border py-3"
            >
              <div className="min-w-0 flex-1">
                <p className={cn("truncate text-sm", key.revoked_at ? "text-muted-foreground" : "text-foreground")}>
                  {key.name}
                  {key.revoked_at ? <span className="ml-2 text-xs text-muted-foreground">Revoked</span> : null}
                </p>
              </div>
              <p className="hidden shrink-0 text-xs text-muted-foreground sm:block">
                {key.last_used_at
                  ? `Last used ${new Date(key.last_used_at).toLocaleDateString()}`
                  : "Never used"}
                {" · "}
                {new Date(key.created_at).toLocaleDateString()}
              </p>
              {!key.revoked_at ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                      disabled={rotatingId === key.id || revokingId === key.id}
                    >
                      {rotatingId === key.id || revokingId === key.id ? (
                        <Spinner className="size-3.5" />
                      ) : (
                        <EllipsisIcon size={14} />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onSelect={() => setManageGrantsFor(key)}>
                      Manage access
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => void handleRotate(key)}>
                      Rotate
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() => void handleRevoke(key)}
                    >
                      Revoke
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="size-7 shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>Name it after what will use it, e.g. &ldquo;Zapier&rdquo;.</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Key name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void handleCreate(); }}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="button" disabled={!createName.trim() || creating} onClick={() => void handleCreate()}>
              {creating ? <Spinner className="size-3.5" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RevealValueDialog
        open={revealTitle !== null}
        onOpenChange={(open) => { if (!open) setRevealTitle(null); }}
        title={revealTitle ?? ""}
        value={revealValue}
        url={revealUrl}
      />

      {manageGrantsFor ? (
        <ManageGrantsDialog
          open={manageGrantsFor !== null}
          onOpenChange={(open) => { if (!open) setManageGrantsFor(null); }}
          keyId={manageGrantsFor.id}
          keyName={manageGrantsFor.name}
        />
      ) : null}
    </>
  );
}
